import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { prefixer } from 'stylis';
import rtlPlugin from 'stylis-plugin-rtl';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useAuthStore } from './stores/auth.store';
import { useNotificationStore } from './stores/notification.store';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import oneSignalService from './services/onesignal.service';
import { initAnalytics } from './helpers/analytics.helper';
import Login from './pages/Login';
import Layout from './components/Layout';
import AppI18nProvider from './components/AppI18nProvider';
import { appI18n, getLanguageDir } from './i18n/app';
import { createAppTheme } from './theme';
import './i18n/app';

// Orders
import PickupOrdersActive from './pages/pickup_orders/PickupOrdersActive';
import PickupOrdersCreate from './pages/pickup_orders/PickupOrdersCreate';

// Transportation
import FleetManagement from './pages/transportation/FleetManagement';
import TruckDetails from './pages/transportation/TruckDetails';
import RouteBuilder from './pages/transportation/RouteBuilder';
import RouteDetails from './pages/transportation/RouteDetails';
import TransportationRoutes from './pages/transportation/TransportationRoutes';
import Drivers from './pages/transportation/Drivers';
import DriverDetail from './pages/transportation/DriverDetail';

// Facilities
import Facilities from './pages/facilities/Facilities';
import FacilityDetails from './pages/facilities/FacilityDetails';

// Processing
import ProcessingCenters from './pages/processing/ProcessingCenters';
import ProcessingQueue from './pages/processing/ProcessingQueue';
import QualityControl from './pages/processing/QualityControl';

// Recycling
import SortingLines from './pages/recycling/SortingLines';
import MaterialTypes from './pages/recycling/MaterialTypes';
import RecyclingQuality from './pages/recycling/Quality';

// Containers
import ContainerList from './pages/containers/ContainerList';
import CollectionSites from './pages/containers/CollectionSites';
import IoTensors from './pages/containers/IoTensors';

// Suppliers
import CollectionPoints from './pages/collection-points/CollectionPoints';
import Zones from './pages/collection-points/Zones';
import DriverRequestsMonitor from './pages/monitoring/DriverRequestsMonitor';
import OrdersMonitoring from './pages/monitoring/OrdersMonitoring';
import OrderApprovals from './pages/monitoring/OrderApprovals';
import Trips from './pages/monitoring/Trips';
import TripDetails from './pages/monitoring/TripDetails';
import OrderDetails from './pages/orders/OrderDetails';
import Suppliers from './pages/suppliers/Suppliers';
import SupplierDetails from './pages/suppliers/SupplierDetails';

// Support
import HelpI18nProvider from './components/help/HelpI18nProvider';
import KnowledgeBase from './pages/support/KnowledgeBase';
import KnowledgeBaseArticle from './pages/support/KnowledgeBaseArticle';
import Notifications from './pages/Notifications';
import PrivacyPolicy from './pages/PrivacyPolicy';
import './i18n/help';

// Reset & Forgot Passowrd
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import OrganizationOnboardingPage from './pages/onboarding/OrganizationOnboardingPage';
import { useOrganizationStore } from './stores/organization.store';
import { getOpsHomePath } from './utils/opsHomePath';

const queryClient = new QueryClient();

const ltrCache = createCache({ key: 'mui', stylisPlugins: [prefixer] });
const rtlCache = createCache({
  key: 'mui-rtl',
  stylisPlugins: [prefixer, rtlPlugin],
});

function App() {
  const { isAuthenticated, initAuth, tokenPayload } = useAuthStore();
  const isPilot = tokenPayload?.is_pilot === true;
  const selectedOrganizationType = useOrganizationStore(
    (state) => state.selectedOrganization?.organization_type,
  );
  const defaultPath = isPilot
    ? '/pickup_orders'
    : getOpsHomePath(
        selectedOrganizationType,
        sessionStorage.getItem('selectedOrganization'),
      );
  const oneSignalInitialized = useRef(false);
  const [mode, setMode] = useState<'light' | 'dark'>(() => {
    const savedMode = localStorage.getItem('themeMode');
    return (savedMode as 'light' | 'dark') || (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light');
  });

  const [direction, setDirection] = useState<'ltr' | 'rtl'>(() => getLanguageDir());

  const toggleTheme = useCallback(() => {
    setMode((prevMode) => {
      const newMode = prevMode === 'light' ? 'dark' : 'light';
      localStorage.setItem('themeMode', newMode);
      return newMode;
    });
  }, []);

  useEffect(() => {
    const onLanguageChanged = (lng: string) => setDirection(getLanguageDir(lng));
    appI18n.on('languageChanged', onLanguageChanged);
    return () => {
      appI18n.off('languageChanged', onLanguageChanged);
    };
  }, []);

  const theme = useMemo(() => createAppTheme(mode, direction), [mode, direction]);

  useEffect(() => {
    initAnalytics();
    void initAuth();
  }, []);

  useEffect(() => {
    if (!isAuthenticated || isPilot || oneSignalInitialized.current) return;
    oneSignalInitialized.current = true;
    oneSignalService.initialize();
  }, [isAuthenticated, isPilot]);

  // Handle notification clicks - mark as read in our system
  useEffect(() => {
    if (isAuthenticated && !isPilot) {
      // Wait for OneSignal to be ready and set up handler
      const setupHandler = async () => {
        if (!oneSignalService.isReady()) {
          // Wait a bit and try again
          setTimeout(setupHandler, 1000);
          return;
        }

        // The service already handles navigation, we also mark as read
        const handleNotificationClick = async (notification: any) => {
          const notificationId = notification.id || notification.notificationId;
          
          // Get selected organization
          const savedOrg = sessionStorage.getItem('selectedOrganization');
          let organizationId: string | null = null;
          
          if (savedOrg) {
            try {
              const parsedOrg = JSON.parse(savedOrg);
              organizationId = parsedOrg.id;
            } catch (e) {
              console.error('Error parsing saved organization:', e);
            }
          }
          
          // Mark notification as read
          if (organizationId && notificationId) {
            try {
              await useNotificationStore.getState().markAsRead(organizationId, notificationId);
            } catch (error) {
              console.error('Error marking notification as read:', error);
            }
          }
        };

        // Set custom handler for marking as read
        oneSignalService.setCustomClickHandler(handleNotificationClick);
      };

      setupHandler();
    }
  }, [isAuthenticated, isPilot]);



  
  return (
    <QueryClientProvider client={queryClient}>
      <AppI18nProvider>
      <CacheProvider value={direction === 'rtl' ? rtlCache : ltrCache}>
      <ThemeProvider theme={theme}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <CssBaseline />
          <Router>
          <Routes>
            <Route path="/login" element={!isAuthenticated ? <Login /> : <Navigate to={defaultPath} replace />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route
              path="/onboarding/organization"
              element={
                isAuthenticated && isPilot ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <OrganizationOnboardingPage />
                  </Layout>
                ) : (
                  <OrganizationOnboardingPage />
                )
              }
            />

            {/* Supplier Routes */}
            <Route
              path="/suppliers"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <Suppliers />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/monitoring/orders"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <OrdersMonitoring />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/monitoring/approvals"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <OrderApprovals />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/monitoring/trips"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <Trips />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/monitoring/trips/:runId"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <TripDetails />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/orders/:pickupOrderId"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <OrderDetails />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/monitoring/driver-requests"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <DriverRequestsMonitor />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/suppliers/collection-points"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <CollectionPoints />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/suppliers/zones"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <Zones />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/suppliers/:supplierId"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <SupplierDetails />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />

            {/* Orders Routes */}
            <Route
              path="/pickup_orders"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <PickupOrdersActive />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/pickup_orders/create"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <PickupOrdersCreate />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/pickup_orders/:orderId/edit"
              element={
                isAuthenticated ? (
                  isPilot ? (
                    <Layout onThemeToggle={toggleTheme}>
                      <Navigate to="/pickup_orders" replace />
                    </Layout>
                  ) : (
                    <Navigate to="/pickup_orders" replace />
                  )
                ) : (
                  <Navigate to="/login" />
                )
              }
            />

            {/* Transportation Routes */}
            <Route
              path="/transportation/fleet"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <FleetManagement />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/transportation/fleet/:truckId"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <TruckDetails />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/transportation/drivers"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <Drivers />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/transportation/drivers/:driverId"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <DriverDetail />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/transportation/routes"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <TransportationRoutes />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/transportation/routes/new"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme} fullBleed>
                    <RouteBuilder />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/transportation/routes/:routeId/edit"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme} fullBleed>
                    <RouteBuilder />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/transportation/routes/:routeId"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <RouteDetails />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/transportation/facilities"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <Facilities />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/transportation/facilities/:facilityId"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <FacilityDetails />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />

            {/* Processing Routes */}
            <Route
              path="/processing/centers"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <ProcessingCenters />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/processing/queue"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <ProcessingQueue />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/processing/quality"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <QualityControl />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />

            {/* Recycling Routes */}
            <Route
              path="/recycling/sorting"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <SortingLines />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/recycling/materials"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <MaterialTypes />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/recycling/quality"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <RecyclingQuality />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />

            {/* Container Routes */}
            <Route
              path="/containers/list"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <ContainerList />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/containers/sites"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <CollectionSites />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/containers/sensors"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <IoTensors />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />

            <Route
              path="/settings/users"
              element={
                isAuthenticated && isPilot ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <Navigate to="/pickup_orders" replace />
                  </Layout>
                ) : (
                  <Navigate to="/pickup_orders" replace />
                )
              }
            />

            <Route
              path="/notifications"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <Notifications />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />

            <Route
              path="/knowledge-base"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <HelpI18nProvider>
                      <KnowledgeBase />
                    </HelpI18nProvider>
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/knowledge-base/:articleId"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <HelpI18nProvider>
                      <KnowledgeBaseArticle />
                    </HelpI18nProvider>
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />

            <Route
              path="/privacy-policy"
              element={<PrivacyPolicy />}
            />

            {/* Default redirect */}
            <Route path="/" element={<Navigate to={defaultPath} replace />} />
            <Route
              path="*"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <div />
                  </Layout>
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
          </Routes>
        </Router>
        </LocalizationProvider>
      </ThemeProvider>
      </CacheProvider>
      </AppI18nProvider>
    </QueryClientProvider>
  );
}

export default App; 
